/**
 * CommunicationsTab — SUN-70. Sub-tabs (Manak, 2026-10-20): Channels · Mails · WhatsApp / Telegram · Connections.
 *
 *   Channels   = the Team chat, with agents as channel members (SUN-71, TeamTab).
 *   Mails      = AURA keeps no log of sent mails yet → an honest "Nothing sent yet". Never fake rows.
 *   WhatsApp / Telegram = the real ChatKart send log (GET /integrations/chatkart/send/logs).
 *                Telegram has no bot yet → its own honest empty line.
 *   (Connections — the "Connect your Claude" set-up and the agent map — is dev-only and not on live.)
 */
import { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { TeamTab } from './TeamTab';

export type CommsSub = 'channels' | 'mails' | 'chat';

const SUBS: { key: CommsSub; label: string }[] = [
  { key: 'channels', label: 'Channels' },
  { key: 'mails', label: 'Mails' },
  { key: 'chat', label: 'WhatsApp / Telegram' },
];

export const NOTHING_SENT = 'Nothing sent yet';

export interface CommunicationsTabProps {
  /** Set-up buttons in Connections are for admins only. */
  isAdmin: boolean;
  /** May call agents in channels, manage members, stop agents (kiaan_manage_agents). */
  canCallAgents?: boolean;
  initialSub?: CommsSub;
}

export function CommunicationsTab({ isAdmin: _isAdmin, canCallAgents = false, initialSub = 'channels' }: CommunicationsTabProps) {
  const [sub, setSub] = useState<CommsSub>(initialSub);
  return (
    <>
      <div className="kw-tabs kw-sub" role="tablist" aria-label="Communications">
        {SUBS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={sub === s.key}
            className={`kw-tab${sub === s.key ? ' kw-sub-on' : ''}`}
            onClick={() => setSub(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>
      {sub === 'channels' && <TeamTab canCallAgents={canCallAgents} />}
      {sub === 'mails' && <MailsLog />}
      {sub === 'chat' && <WhatsAppLog />}
    </>
  );
}

function MailsLog() {
  return (
    <div className="kw-scroll kw-pad" data-testid="comms-mails">
      <h4 className="kw-h4">Mails sent by agents and the team</h4>
      <div className="kw-empty">
        <b>{NOTHING_SENT}</b>
        <div className="kw-small">AURA does not keep a log of sent mails yet. When it does, every mail shows here.</div>
      </div>
    </div>
  );
}

export interface WaLogRow {
  id: string;
  entityType: string;
  recipientName?: string | null;
  recipientPhone?: string | null;
  templateName?: string | null;
  status: string;
  errorReason?: string | null;
  sentAt?: string | null;
  createdAt: string;
}

const WHAT: Record<string, string> = {
  proposal: 'Proposal', invoice: 'Invoice', payment_link: 'Payment link', dispatch: 'Dispatch', delivery: 'Delivery', service_ticket: 'Service ticket',
};
const PILL: Record<string, string> = { sent: 'kw-ok', pending: 'kw-wt', failed: 'kw-bad' };

/** Only the last 4 digits: the list is a report, not a phone book. */
export function maskPhone(p?: string | null): string {
  const d = (p ?? '').replace(/\D/g, '');
  return d.length >= 4 ? `••••${d.slice(-4)}` : '';
}

function istTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
}

function WhatsAppLog() {
  const [rows, setRows] = useState<WaLogRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/integrations/chatkart/send/logs', { params: { limit: 50 } })
      .then((res) => {
        if (!alive) return;
        const d = res.data?.data;
        setRows(Array.isArray(d) ? (d as WaLogRow[]) : []);
      })
      .catch(() => { if (alive) { setFailed(true); setRows([]); } });
    return () => { alive = false; };
  }, []);

  return (
    <div className="kw-scroll kw-pad" data-testid="comms-chat">
      <h4 className="kw-h4">WhatsApp (ChatKart) — what AURA sent</h4>
      {rows === null && <div className="kw-small">Loading…</div>}
      {failed && <div className="kw-banner" role="status">Could not load the WhatsApp log. AURA works as normal.</div>}
      {rows && !failed && rows.length === 0 && (
        <div className="kw-empty"><b>{NOTHING_SENT}</b></div>
      )}
      {rows && rows.length > 0 && (
        <table className="sm-table" data-testid="wa-log">
          <thead><tr><th>When</th><th>What</th><th>To</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid="wa-row">
                <td>{istTime(r.sentAt ?? r.createdAt)}</td>
                <td>{WHAT[r.entityType] ?? r.entityType}</td>
                <td>{[r.recipientName, maskPhone(r.recipientPhone)].filter(Boolean).join(' · ') || '—'}</td>
                <td>
                  <span className={`kw-pill ${PILL[r.status] ?? ''}`} title={r.errorReason ?? undefined}>{r.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h4 className="kw-h4" style={{ marginTop: 16 }}>Telegram</h4>
      <div className="kw-empty">
        <b>{NOTHING_SENT}</b>
        <div className="kw-small">The Telegram bot is not set up yet.</div>
      </div>
    </div>
  );
}
